import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Zap, Plus, Trash2, BookOpen } from 'lucide-react';
import { ReplacementResult, addCustomFix, removeCustomFix, getCustomFixes, getCustomFixesFromStorage, PronunciationFix } from '@/utils/pronunciationFixes';
import { Button, IconButton } from './ui/Button';
import { Input, Checkbox } from './ui/Form';
import { Badge } from './ui/Badge';

interface PronunciationFixModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: ReplacementResult | null;
  onApply: (fixedLyrics: string) => void;
  onRefresh: () => void;
}

export const PronunciationFixModal: React.FC<PronunciationFixModalProps> = ({
  isOpen,
  onClose,
  result,
  onApply,
  onRefresh
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOriginal, setNewOriginal] = useState('');
  const [newReplacement, setNewReplacement] = useState('');
  const [newReason, setNewReason] = useState('');
  const [showCustomList, setShowCustomList] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [customFixes, setCustomFixes] = useState<PronunciationFix[]>(getCustomFixesFromStorage());

  // Load custom fixes from API on mount
  useEffect(() => {
    getCustomFixes().then(setCustomFixes).catch(() => {});
  }, []);

  // Initialize all as selected when modal opens (MUST be before early return)
  useEffect(() => {
    if (result && result.changes.length > 0) {
      setSelectedIndices(new Set(result.changes.map((_, idx) => idx)));
    }
  }, [result]);

  if (!isOpen || !result) return null;

  const hasChanges = result.changes.length > 0;

  const toggleSelection = (index: number) => {
    const newSet = new Set(selectedIndices);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedIndices(newSet);
  };

  const selectAll = () => {
    setSelectedIndices(new Set(result.changes.map((_, idx) => idx)));
  };

  const deselectAll = () => {
    setSelectedIndices(new Set());
  };

  const handleAddCustom = () => {
    if (!newOriginal.trim() || !newReplacement.trim()) return;

    const fix = {
      original: newOriginal.trim(),
      replacement: newReplacement.trim(),
      reason: newReason.trim() || 'Custom pronunciation fix'
    };

    addCustomFix(fix).then(() => {
      getCustomFixes().then(setCustomFixes).catch(() => {});
    });

    setNewOriginal('');
    setNewReplacement('');
    setNewReason('');
    setShowAddForm(false);
    onRefresh();
  };

  const handleRemoveCustom = (original: string) => {
    removeCustomFix(original).then(() => {
      getCustomFixes().then(setCustomFixes).catch(() => {});
    });
    onRefresh();
  };

  const handleApply = () => {
    // Apply only selected replacements
    let fixedLyrics = result.original;

    // Group changes by word to handle multiple occurrences
    const selectedChanges = result.changes.filter((_, idx) => selectedIndices.has(idx));

    // Apply each selected replacement
    selectedChanges.forEach(change => {
      const regex = new RegExp(change.word, 'gi');
      fixedLyrics = fixedLyrics.replace(regex, (match) => {
        // Preserve capitalization pattern
        if (match[0] === match[0].toUpperCase()) {
          return change.replacement.charAt(0).toUpperCase() + change.replacement.slice(1);
        }
        return change.replacement;
      });
    });

    onApply(fixedLyrics);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
      <div className="bg-slate-900 border-2 border-slate-700 rounded-2xl max-w-3xl w-full max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent rounded-lg text-slate-950">
              <Zap size={20} />
            </div>
            <div>
              <h2 className="text-xl font-black uppercase tracking-tight text-slate-50">
                Pronunciation Scanner
              </h2>
              <p className="text-xs text-slate-400 uppercase tracking-widest font-bold mt-1">
                Suno-Optimized Phonetic Replacements
              </p>
            </div>
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </IconButton>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Custom Dictionary Management */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowCustomList(!showCustomList)}
                leftIcon={<BookOpen size={16} />}
                className="text-blue-400 hover:text-blue-300"
              >
                My Custom Dictionary ({customFixes.length})
              </Button>
              <Button
                variant="success"
                size="xs"
                onClick={() => setShowAddForm(!showAddForm)}
                leftIcon={<Plus size={14} />}
                className="bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30"
              >
                Add Custom Word
              </Button>
            </div>

            {showCustomList && customFixes.length > 0 && (
              <div className="space-y-2 bg-slate-800/40 border border-slate-700 rounded-xl p-4">
                {customFixes.map((fix, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-900/40 rounded-lg">
                    <div className="flex items-center gap-3 flex-1">
                      <Badge variant="danger-muted" size="sm" className="font-mono">
                        {fix.original}
                      </Badge>
                      <span className="text-slate-400">→</span>
                      <Badge variant="success-muted" size="sm" className="font-mono">
                        {fix.replacement}
                      </Badge>
                      {fix.reason && (
                        <span className="text-xs text-slate-400 italic">({fix.reason})</span>
                      )}
                    </div>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      onClick={() => handleRemoveCustom(fix.original)}
                      aria-label={`Remove custom fix for ${fix.original}`}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}

            {showAddForm && (
              <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4 space-y-3">
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Add Custom Pronunciation
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Problem Word</label>
                    <Input
                      type="text"
                      value={newOriginal}
                      onChange={(e) => setNewOriginal(e.target.value)}
                      placeholder="e.g. Ocmulgee"
                      size="sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Phonetic Spelling</label>
                    <Input
                      type="text"
                      value={newReplacement}
                      onChange={(e) => setNewReplacement(e.target.value)}
                      placeholder="e.g. Oakmullghee"
                      size="sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Reason (optional)</label>
                  <Input
                    type="text"
                    value={newReason}
                    onChange={(e) => setNewReason(e.target.value)}
                    placeholder="e.g. Suno mispronounces this river name"
                    size="sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddCustom}
                    disabled={!newOriginal.trim() || !newReplacement.trim()}
                  >
                    Add to Dictionary
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {hasChanges ? (
            <>
              <div className="flex items-center justify-between gap-2 text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle size={18} />
                  <span className="text-sm font-bold">
                    Found {result.changes.length} pronunciation issue{result.changes.length !== 1 ? 's' : ''} in current lyrics
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="link"
                    size="xs"
                    onClick={selectAll}
                    className="text-green-400 hover:text-green-300"
                  >
                    Select All
                  </Button>
                  <span className="text-slate-400">|</span>
                  <Button
                    variant="link"
                    size="xs"
                    onClick={deselectAll}
                    className="text-red-400 hover:text-red-300"
                  >
                    Deselect All
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">
                  Select Replacements to Apply
                </h3>
                <div className="space-y-2">
                  {result.changes.map((change, idx) => (
                    <div
                      key={idx}
                      className={`bg-slate-800/40 border rounded-xl p-4 space-y-2 transition-all cursor-pointer ${
                        selectedIndices.has(idx)
                          ? 'border-accent/50 bg-accent/5'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                      onClick={() => toggleSelection(idx)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={selectedIndices.has(idx)}
                            onChange={() => toggleSelection(idx)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="text-red-400 font-mono font-bold line-through">
                            {change.word}
                          </span>
                          <span className="text-slate-400">→</span>
                          <span className="text-green-400 font-mono font-bold">
                            {change.replacement}
                          </span>
                        </div>
                        <Badge variant="default-muted" size="xs" className="font-mono">
                          Line {change.lineNumber}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 italic ml-7">
                        {change.reason}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
                <h4 className="text-xs font-black uppercase tracking-widest text-blue-400 mb-2">
                  Preview (First 500 chars)
                </h4>
                <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {result.fixed.substring(0, 500)}
                  {result.fixed.length > 500 && '...'}
                </pre>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-green-500/10 rounded-full mb-4">
                <CheckCircle size={48} className="text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-slate-200 mb-2">
                No Issues Found!
              </h3>
              <p className="text-sm text-slate-400 max-w-md">
                Your lyrics don't contain any known Suno pronunciation problems. You're good to go!
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700 flex items-center justify-between gap-4">
          <div className="text-xs text-slate-400 flex items-center gap-2">
            <AlertCircle size={14} />
            <span>
              {hasChanges
                ? `${selectedIndices.size} of ${result.changes.length} replacement${selectedIndices.size !== 1 ? 's' : ''} selected`
                : 'No changes to apply'}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="md"
              onClick={onClose}
              className="uppercase tracking-tight"
            >
              Cancel
            </Button>
            {hasChanges && (
              <Button
                variant="primary"
                size="md"
                onClick={handleApply}
                disabled={selectedIndices.size === 0}
                className="uppercase tracking-tight shadow-lg shadow-amber-900/20"
              >
                Apply {selectedIndices.size > 0 ? `${selectedIndices.size} Fix${selectedIndices.size !== 1 ? 'es' : ''}` : 'Fixes'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
